import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Descriptions, Empty, Popconfirm, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import type { ColumnsType } from 'antd/es/table';
import {
  PAYMENT_FORM_LABEL,
  RECEIVABLE_TYPE_LABEL,
  billingApi,
  maskOrderMobile,
  type OrderDetail,
  type OrderItem,
  type OrderPaymentTransaction,
  type OrderProductSnapshot,
  type ReceivablePlan,
} from '../../api/billing.api';
import { useHasPermission } from '../../auth/HasPermission';
import { useDraggableModal } from '../../components/useDraggableModal';
import { startAction } from '../../observability/otel';
import { useSession } from '../../auth/SessionContext';
import { ApiError } from '../../api/transport';
import { paymentEntriesApi } from '../../api/payment-entries.api';
import {
  PAYMENT_ENTRY_STATUS_LABEL,
  PAYMENT_ENTRY_TYPE_LABEL,
  type PaymentEntryType,
  type PaymentEntryView,
} from '../../types/payment.types';
import { DetailDrawer } from '../../components/DetailDrawer';
import { MoneyDisplay } from '../../components/MoneyDisplay';
import { PaymentEntryDialog } from '../../components/payment/PaymentEntryDialog';
import { OfflinePaymentRecordDialog } from './OfflinePaymentRecordDialog';
import { RefundRecordDialog } from './RefundRecordDialog';
import { ORDER_STATUS_LABEL } from '../../api/billing.api';
import { OrderStatusTag } from './OrderStatusTag';
import { DunningModal } from './DunningModal';
import { EnrollmentDetailDrawer } from '../students/EnrollmentDetailDrawer';
import {
  entitlementLedgerApi,
  type LedgerEntryDetail,
  type LedgerEntryListItem,
} from '../../api/entitlementLedger.api';
import { LedgerDetailDrawer, ledgerTypeLabel } from '../deduction/LedgerDetailDrawer';
import { PERIOD_LESSON_KIND_LABEL } from '../../utils/period-ledger';

const { Text } = Typography;

function formatTime(value: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '-';
}

/** 是否可支付状态（原型：收款链接 / 二维码 / 登记收款 / 取消订单的显示条件）。 */
function isOrderPayable(order: OrderDetail): boolean {
  return order.status === 'PENDING_PAYMENT' || order.status === 'PARTIALLY_PAID';
}

/** 支付流水渠道展示名；未知渠道原样展示编码。 */
const PAYMENT_CHANNEL_LABEL: Record<string, string> = {
  WECHAT: '微信',
  ALIPAY: '支付宝',
  CASH: '现金',
  BANK_TRANSFER: '银行转账',
  OTHER: '其他',
  WECHAT_PAY: '微信支付',
};

/** 支付流水状态展示（对齐后端 payment-transaction-status 状态机，未知值原样展示）。 */
const PAYMENT_TXN_STATUS_META: Record<string, { text: string; color: string }> = {
  INITIATED: { text: '已发起', color: 'blue' },
  PENDING: { text: '处理中', color: 'blue' },
  SUCCEEDED: { text: '已成功', color: 'green' },
  FAILED: { text: '已失败', color: 'red' },
  CLOSED: { text: '已关闭', color: 'default' },
  REVERSED: { text: '已冲正', color: 'red' },
};

/** 应收节点状态展示名。 */
const PLAN_STATUS_LABEL: Record<ReceivablePlan['status'], string> = {
  PENDING: '待收',
  SETTLED: '已结清',
  CANCELLED: '已取消',
};

/* ------------------------------ 各页签表格列定义 ------------------------------ */

const snapshotSkuColumns: ColumnsType<OrderItem> = [
  { title: 'SKU名称', dataIndex: ['skuSnapshot', 'skuName'] },
  {
    // O-14：售卖形态快照列（历史快照缺省按课时包渲染）
    title: '售卖形态',
    width: 90,
    align: 'center',
    render: (_, item) =>
      (item.skuSnapshot as { chargeMode?: string }).chargeMode === 'PERIOD' ? '期限卡' : '课时包',
  },
  {
    // O-14：课时/期限列——期限行「N 天（畅学）」，课时行「N 课时」
    title: '课时/期限',
    width: 110,
    align: 'center',
    render: (_, item) => {
      const snap = item.skuSnapshot as {
        chargeMode?: string;
        lessonCount?: number;
        periodDays?: number;
      };
      return snap.chargeMode === 'PERIOD'
        ? `${snap.periodDays ?? '-'} 天（畅学）`
        : `${snap.lessonCount ?? '-'} 课时`;
    },
  },
  {
    title: '行标价',
    dataIndex: 'unitPrice',
    width: 110,
    render: (value: number) => <MoneyDisplay amountInCents={value} />,
  },
  {
    title: '试课次数',
    width: 90,
    align: 'center',
    render: (_, item) => (item.skuSnapshot as { trialSessionCount?: number }).trialSessionCount ?? '-',
  },
  { title: '数量', dataIndex: 'quantity', width: 70, align: 'center' },
  {
    title: '理论金额',
    dataIndex: 'amount',
    width: 110,
    render: (value: number) => <MoneyDisplay amountInCents={value} />,
  },
];

const planColumns: ColumnsType<ReceivablePlan> = [
  { title: '类型', dataIndex: 'type', render: (value: ReceivablePlan['type']) => RECEIVABLE_TYPE_LABEL[value] },
  { title: '金额', dataIndex: 'amount', render: (value: number) => <MoneyDisplay amountInCents={value} /> },
  { title: '已付', dataIndex: 'paidAmount', render: (value: number) => <MoneyDisplay amountInCents={value} /> },
  {
    title: '待付',
    render: (_, plan) => <MoneyDisplay amountInCents={plan.amount - plan.paidAmount} />,
  },
  { title: '到期日', dataIndex: 'dueAt', render: (value: string | null) => formatTime(value) },
  {
    title: '状态',
    dataIndex: 'status',
    render: (value: ReceivablePlan['status'], record) =>
      record.overdue && value === 'PENDING' ? <Tag color="red">已逾期</Tag> : PLAN_STATUS_LABEL[value],
  },
];

const paymentTxnColumns: ColumnsType<OrderPaymentTransaction> = [
  { title: '渠道', dataIndex: 'channel', width: 100, render: (value: string) => PAYMENT_CHANNEL_LABEL[value] ?? value },
  { title: '渠道流水', dataIndex: 'channelTxnNo', width: 150, ellipsis: true, render: (value: string | null) => value ?? '-' },
  { title: '金额', dataIndex: 'amountMinor', width: 110, render: (value: number) => <MoneyDisplay amountInCents={value} /> },
  {
    title: '状态',
    dataIndex: 'status',
    width: 90,
    render: (value: string) => {
      const meta = PAYMENT_TXN_STATUS_META[value];
      return meta ? <Tag color={meta.color}>{meta.text}</Tag> : value;
    },
  },
  { title: '经办人', width: 110, render: (_, record) => record.operator.name ?? '线上支付' },
  { title: '支付时间', dataIndex: 'paidAt', width: 170, render: formatTime },
  { title: '业务支付号', dataIndex: 'businessPaymentNo', width: 200, ellipsis: true },
  {
    title: '回单',
    dataIndex: 'receiptFileKey',
    width: 110,
    render: (value: string | null) => (value ? <Tag color="green">已附回单</Tag> : '-'),
  },
  { title: '备注', dataIndex: 'remark', ellipsis: true, render: (value: string | null) => value ?? '-' },
];

const paymentEntryColumns: ColumnsType<PaymentEntryView> = [
  {
    title: '类型',
    dataIndex: 'entryType',
    width: 110,
    render: (value: PaymentEntryType) => PAYMENT_ENTRY_TYPE_LABEL[value],
  },
  {
    title: '款项',
    width: 200,
    render: (_, entry) => {
      const summary = entry.receivablePlanSummary;
      if (summary) return RECEIVABLE_TYPE_LABEL[summary.type];
      return entry.allowDeposit ? '首付款或全款（家长自选）' : '仅全款';
    },
  },
  {
    title: '允许只付首付款',
    width: 130,
    render: (_, entry) => (entry.receivablePlanId ? '-' : entry.allowDeposit ? '是' : '否'),
  },
  { title: '生成员工', dataIndex: 'createdByName', width: 100, render: (value: string) => value || '-' },
  { title: '生成时间', dataIndex: 'createdAt', width: 170, render: formatTime },
  {
    title: '状态',
    width: 90,
    render: (_, entry) => {
      const status = entry.derivedStatus ?? entry.status;
      const color = status === 'PAID' ? 'green' : status === 'CLOSED' ? 'default' : 'gold';
      return <Tag color={color}>{PAYMENT_ENTRY_STATUS_LABEL[status]}</Tag>;
    },
  },
];

/* -------------------------------- 页签子组件 -------------------------------- */

/** 商品快照页签：商品维度信息（下单时快照）+ SKU 明细，结构对齐原型。 */
function ProductSnapshotTab({ snapshot, items }: { snapshot: OrderProductSnapshot; items: OrderItem[] }) {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="商品名称">{snapshot.name}</Descriptions.Item>
        <Descriptions.Item label="地区">{`${snapshot.provinceName} ${snapshot.cityName}`}</Descriptions.Item>
        <Descriptions.Item label="卖点" span={2}>{snapshot.features.join('，') || '-'}</Descriptions.Item>
        <Descriptions.Item label="首付比例">{`${Math.round(snapshot.depositRatio * 100)}%`}</Descriptions.Item>
        <Descriptions.Item label="尾款周期">{`首付款结清后 ${snapshot.finalDueDays} 天内交齐`}</Descriptions.Item>
      </Descriptions>
      <Table
        size="small"
        rowKey="orderItemId"
        columns={snapshotSkuColumns}
        dataSource={items}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    </Space>
  );
}

/**
 * 课时变化页签（O-14 period-pass-admin-ui:SC-06；BS-171 改服务端订单维度筛选）。
 *
 * 展示与本单相关的权益流水：服务端流水列表按 `businessRefId=本单 ID` 精确筛选
 * （businessRefType=ORDER 行的 businessRefId 即订单 ID，含期限卡开卡行），
 * 不再客户端过滤——学生流水超过页大小时本单相关行不再被静默截断。
 * PERIOD 行课时类别「期限卡事件（不扣课时）」、三组数量「前→后」列以「—」呈现；
 * 点击流水号下钻详情（PERIOD 行以「期限卡事件」单项替代三组数量快照）。
 */
function OrderLedgerTab({ orderId, studentId }: { orderId: string; studentId: string }) {
  const [rows, setRows] = useState<LedgerEntryListItem[] | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<LedgerEntryDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    entitlementLedgerApi
      .listEntries({ studentId, businessRefId: orderId, page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        setRows(res.items);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, studentId]);

  // 流水号下钻：加载详情（404/失败静默关闭，详情抽屉只读）
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      return;
    }
    void (async () => {
      try {
        setDetail(await entitlementLedgerApi.getEntry(detailId));
      } catch {
        setDetail(null);
      }
    })();
  }, [detailId]);

  const columns: ColumnsType<LedgerEntryListItem> = [
    {
      title: '流水号',
      dataIndex: 'id',
      width: 180,
      ellipsis: true,
      render: (value: string) => <a onClick={() => setDetailId(value)}>{value}</a>,
    },
    {
      title: '变动事由',
      dataIndex: 'type',
      width: 110,
      render: (value: string) => ledgerTypeLabel[value] ?? value,
    },
    {
      title: '课时类别',
      width: 140,
      render: (_, record) =>
        record.lessonKind === 'PERIOD'
          ? PERIOD_LESSON_KIND_LABEL
          : record.lessonKind === 'GIFTED'
            ? '赠送课'
            : '正式课',
    },
    {
      title: '赠送课时（前→后）',
      width: 130,
      render: (_, record) =>
        record.lessonKind === 'PERIOD' ? '—' : `${record.giftedBefore} → ${record.giftedAfter}`,
    },
    {
      title: '购买课时（前→后）',
      width: 130,
      render: (_, record) =>
        record.lessonKind === 'PERIOD' ? '—' : `${record.formalBefore} → ${record.formalAfter}`,
    },
    {
      title: '可用课时（前→后）',
      width: 130,
      render: (_, record) =>
        record.lessonKind === 'PERIOD'
          ? '—'
          : `${record.formalBefore + record.giftedBefore} → ${record.formalAfter + record.giftedAfter}`,
    },
    { title: '时间', dataIndex: 'createdAt', width: 150, render: (value: string) => formatTime(value) },
  ];

  return (
    <>
      <Table
        size="small"
        rowKey="id"
        loading={rows === null}
        columns={columns}
        dataSource={rows ?? []}
        pagination={false}
        locale={{ emptyText: '暂无权益流水' }}
        scroll={{ x: 'max-content' }}
        data-testid="order-ledger-table"
      />
      <LedgerDetailDrawer detail={detail} onClose={() => setDetailId(null)} />
    </>
  );
}

/** 款项形态未定说明：列出家长可选的两种形态金额（对齐原型应收节点页签空态文案）。 */
function UndeterminedPlansNote({ order }: { order: OrderDetail }) {
  const total = order.receivableAmount;
  const { depositRatio, finalDueDays } = order.productSnapshot;
  const deposit = Math.round(total * depositRatio);
  return (
    <Alert
      type="info"
      showIcon
      message="款项形态未确定：尚未产生应收节点"
      description={
        <div>
          <div>
            · 一次性全款 <MoneyDisplay amountInCents={total} />
          </div>
          {depositRatio > 0 && (
            <div>
              · 分期：首付款 <MoneyDisplay amountInCents={deposit} /> + 尾款{' '}
              <MoneyDisplay amountInCents={total - deposit} />
              （首付款结清后 {finalDueDays} 天内交齐）
            </div>
          )}
        </div>
      }
    />
  );
}

/**
 * 应收节点页签：资金汇总（实收 / 待收 / 款项形态，收纳自原头部）+ 节点明细。
 *
 * 可催缴订单（待支付/部分支付且有当前节点）在当前应收节点行展示「催缴」入口
 * （installment-dunning-admin-ui:SC-03/SC-04）；过期订单呈现重新下单说明。
 */
function ReceivablePlansTab({
  order,
  dunningTargetId,
  onDunning,
}: {
  order: OrderDetail;
  /** 当前应收节点 ID（无催缴条件时为 null，整列不渲染）。 */
  dunningTargetId: string | null;
  onDunning: (plan: ReceivablePlan) => void;
}) {
  const columns: ColumnsType<ReceivablePlan> = dunningTargetId
    ? [
        ...planColumns,
        {
          title: '操作',
          width: 80,
          render: (_, plan) =>
            plan.receivablePlanId === dunningTargetId ? (
              <a onClick={() => onDunning(plan)} data-testid="dunning-entry">
                催缴
              </a>
            ) : null,
        },
      ]
    : planColumns;
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {order.status === 'EXPIRED' && (
        <Alert
          type="warning"
          showIcon
          message="订单已过期，应收节点不再催缴或收款"
          description="过期订单无法继续付款；如家长仍有购买意愿，请重新下单（历史记录保留）。"
          data-testid="expired-plans-alert"
        />
      )}
      <Descriptions bordered size="small" column={3}>
        {/* 实收口径五处同源（order-manual-refund-admin-ui:SC-07）：Σ活跃节点已收 − 累计退款 */}
        <Descriptions.Item label="实收">
          <MoneyDisplay amountInCents={order.netReceivedAmount} />
          {order.refundedAmount > 0 && (
            <Text type="secondary" style={{ marginLeft: 8 }} data-testid="order-refunded-hint">
              已退 <MoneyDisplay amountInCents={order.refundedAmount} />
            </Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="待收"><MoneyDisplay amountInCents={order.outstandingAmount} /></Descriptions.Item>
        <Descriptions.Item label="款项形态">{PAYMENT_FORM_LABEL[order.paymentForm]}</Descriptions.Item>
      </Descriptions>
      {order.receivablePlans.length ? (
        <Table
          size="small"
          rowKey="receivablePlanId"
          columns={columns}
          dataSource={order.receivablePlans}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <UndeterminedPlansNote order={order} />
      )}
    </Space>
  );
}

/** 支付流水页签：列序对齐原型（渠道 / 流水 / 金额 / 状态 / 经办人 / 时间），超出列排其后。 */
function PaymentTransactionsTab({ transactions }: { transactions: OrderPaymentTransaction[] }) {
  if (!transactions.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无支付流水" />;
  }
  return (
    <Table
      size="small"
      rowKey="paymentTransactionId"
      columns={paymentTxnColumns}
      dataSource={transactions}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );
}

/** 收款入口页签：按订单列既有收款入口（后端未上线时开发态走 MSW mock）。 */
function PaymentEntriesTab({ orderId }: { orderId: string }) {
  const [entries, setEntries] = useState<PaymentEntryView[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setFailed(false);
    paymentEntriesApi
      .listPaymentEntries({ orderId })
      .then((list) => {
        if (!cancelled) setEntries(list);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (failed) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="收款入口加载失败" />;
  }
  if (!entries) {
    return <Spin size="small" />;
  }
  if (!entries.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未生成收款入口" />;
  }
  return (
    <Table
      size="small"
      rowKey="id"
      columns={paymentEntryColumns}
      dataSource={entries}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );
}

/* -------------------------------- 主组件 -------------------------------- */

export interface OrderDetailDrawerProps {
  orderId: string;
  onClose: () => void;
  onOrderChanged?: () => void;
  /**
   * 订单不存在（404）时回调，调用方可切换为回退说明（如账本业务编号下钻 SC-16）。
   * 不传时保持原行为（抽屉内 toast 报错）。
   */
  onNotFound?: () => void;
}

/** ADM-10 订单详情抽屉。 */
export function OrderDetailDrawer({ orderId, onClose, onOrderChanged, onNotFound }: OrderDetailDrawerProps) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [paymentEntryType, setPaymentEntryType] = useState<PaymentEntryType | null>(null);
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [dunningPlanId, setDunningPlanId] = useState<string | null>(null);

  // onNotFound 经 ref 转发，调用方可传内联函数而不触发详情重请求
  const notFoundRef = useRef(onNotFound);
  notFoundRef.current = onNotFound;

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      setOrder(await billingApi.getOrder(orderId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        notFoundRef.current?.();
        return;
      }
      message.error(err instanceof Error ? err.message : '订单详情加载失败');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  /** 取消订单（Popconfirm 确认后调用），成功后刷新详情与列表；缺省原因便于留痕审计。 */
  const handleCancelOrder = useCallback(async () => {
    setCancelling(true);
    try {
      await billingApi.cancelOrder(orderId, '管理端取消');
      message.success('已取消');
      await fetchDetail();
      onOrderChanged?.();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '取消订单失败');
    } finally {
      setCancelling(false);
    }
  }, [orderId, fetchDetail, onOrderChanged]);

  /** 删除订单（命令式二次确认，invalid-order-soft-delete D9）：成功关抽屉并刷新列表；409/403 保留界面透传原因。 */
  const showDeleteConfirm = useCallback(() => {
    modal.confirm({
      title: '确认删除该订单？',
      content: `订单号 ${order?.orderNo ?? ''}（${order?.studentName ?? ''}）。删除后列表不再显示，可在订单详情与操作日志追溯。`,
      okText: '确认删除',
      okButtonProps: { danger: true },
      cancelText: '再想想',
      onOk: async () => {
        // 动作级 RUM（frontend-rum §1.2 P0 资金链路）：属性白名单仅 orderId
        const span = startAction('action: billing.softDeleteOrder', { orderId });
        try {
          await billingApi.deleteOrder(orderId);
          span.end();
          message.success('已删除');
          onOrderChanged?.();
          onClose();
        } catch (err) {
          span.end();
          message.error(err instanceof Error ? err.message : '删除失败');
        }
      },
    });
  }, [order?.orderNo, order?.studentName, orderId, onOrderChanged, onClose]);

  /** 复制订单号到剪贴板。 */
  const copyOrderNo = useCallback(async (orderNo: string) => {
    try {
      await navigator.clipboard.writeText(orderNo);
      message.success('已复制订单号');
    } catch {
      message.error('复制失败，请手动复制');
    }
  }, []);

  const canManageBilling = useHasPermission('ADM-09', 'EDIT');
  // 登记退款双闸（order-manual-refund-admin-ui:SC-01/SC-02）：无 REFUND 权限不渲染按钮
  const canRefund = useHasPermission('ADM-09', 'REFUND');
  // 删除订单双闸（order-soft-delete-admin-ui:SC-01/SC-02）：无 DELETE 权限不渲染按钮（非禁用态）
  const canDelete = useHasPermission('ADM-09', 'DELETE');
  // 可删性派生（invalid-order-soft-delete：实收 0 的待支付/已过期/已取消单）；
  // 已付款单不渲染删除入口（staff-billing-workbench:SC-16 同口径），服务端 409 为并发竞态兜底
  const softDeletable =
    !!order &&
    (order.status === 'PENDING_PAYMENT' || order.status === 'EXPIRED' || order.status === 'CANCELLED') &&
    order.paidAmount === 0;
  const modal = useDraggableModal();
  // 经办人摘要格（当前登录员工姓名，ADM-33 只读字段）
  const session = useSession();
  const currentStaffName = session.session?.name ?? '';
  const offlinePaymentBlockedReason = order ? offlinePaymentEntryDisabledReason(order) : null;
  const switched = Boolean(order?.sourceProductId && order.sourceProductId !== order.productId);
  const payable = order ? isOrderPayable(order) : false;
  const sourceApplicationId = order?.linkedApplications[0]?.applicationId ?? null;
  // 催缴目标 = 可支付订单的当前应收节点（最早到期未结清；无 ADM-09 EDIT 权限或款项未定时不渲染入口）
  const dunningTargetId =
    canManageBilling && payable ? order?.currentReceivablePlan?.receivablePlanId ?? null : null;
  const dunningPlan = order?.receivablePlans.find(
    (plan) => plan.receivablePlanId === dunningPlanId,
  ) ?? null;

  return (
    <DetailDrawer
      open
      width={860}
      loading={loading}
      title={order ? `订单详情 · ${order.orderNo}` : '订单详情'}
      onClose={onClose}
      extra={undefined}
    >
      {order && (
        <>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="订单号">
              <Space size={4}>
                {order.orderNo}
                <Button type="link" size="small" onClick={() => void copyOrderNo(order.orderNo)}>
                  复制
                </Button>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="学生">{order.studentName}</Descriptions.Item>
            <Descriptions.Item label="监护人">{maskOrderMobile(order.guardianMobile)}</Descriptions.Item>
            <Descriptions.Item label="校区">{order.campusName}</Descriptions.Item>
            <Descriptions.Item label="来源">{order.source === 'PARENT_SELF' ? '家长自助' : '员工代建'}</Descriptions.Item>
            <Descriptions.Item label="来源报名">
              {sourceApplicationId ? (
                <Button type="link" size="small" onClick={() => setEnrollmentId(sourceApplicationId)}>
                  {sourceApplicationId}
                </Button>
              ) : (
                '无（非报名转入）'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="订单状态"><OrderStatusTag status={order.status} /></Descriptions.Item>
                {(order.refundedAmount > 0 || order.netReceivedAmount !== order.paidAmount) && (
                  <>
                    <Descriptions.Item label="实收（净额）" data-testid="order-net-received">
                      <Text type="danger">
                        <MoneyDisplay amountInCents={order.netReceivedAmount} />
                      </Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        已收 <MoneyDisplay amountInCents={order.paidAmount} /> − 退款{' '}
                        <MoneyDisplay amountInCents={order.refundedAmount} />
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="累计退款">
                      {order.refundedAmount > 0 ? (
                        <MoneyDisplay amountInCents={order.refundedAmount} />
                      ) : (
                        '无'
                      )}
                    </Descriptions.Item>
                  </>
                )}
            <Descriptions.Item label="创建时间">{formatTime(order.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="过期时间">{formatTime(order.expiresAt)}</Descriptions.Item>
            <Descriptions.Item label="原价"><MoneyDisplay amountInCents={order.originalAmount} /></Descriptions.Item>
            <Descriptions.Item label="应收"><MoneyDisplay amountInCents={order.receivableAmount} /></Descriptions.Item>
          </Descriptions>
          {switched && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="本订单相对来源商品发生切换"
              description="报名覆盖与审计页签保留三类 SKU 差异和结构化原因。"
              data-testid="order-switched-alert"
            />
          )}
          {canManageBilling && (
            <Space style={{ marginTop: 12 }} wrap>
              {payable && (
                <>
                  <Button onClick={() => setPaymentEntryType('LINK')}>收款链接</Button>
                  <Button onClick={() => setPaymentEntryType('QR')}>收款二维码</Button>
                </>
              )}
              <Button
                type="primary"
                disabled={Boolean(offlinePaymentBlockedReason)}
                onClick={() => setRecording(true)}
                data-testid="offline-payment-entry"
              >
                收款登记
              </Button>
              {canRefund && (
                <Button danger onClick={() => setRefundOpen(true)} data-testid="order-refund-entry">
                  登记退款
                </Button>
              )}
              {payable && (
                <Popconfirm
                  title="确认取消该订单？取消后可重新下单，历史记录仍保留"
                  okText="确定"
                  cancelText="再想想"
                  onConfirm={() => void handleCancelOrder()}
                  disabled={cancelling}
                >
                  <Button danger loading={cancelling}>
                    取消订单
                  </Button>
                </Popconfirm>
              )}
              {canDelete && softDeletable && !order.deletedAt && (
                <Button danger onClick={showDeleteConfirm} data-testid="order-delete-entry">
                  删除订单
                </Button>
              )}
              {offlinePaymentBlockedReason && (
                <Text type="secondary">{offlinePaymentBlockedReason}</Text>
              )}
            </Space>
          )}
          <Tabs
            style={{ marginTop: 16 }}
            items={[
              {
                key: 'product',
                label: '商品快照',
                children: <ProductSnapshotTab snapshot={order.productSnapshot} items={order.items} />,
              },
              {
                key: 'plans',
                label: '应收节点',
                children: (
                  <ReceivablePlansTab
                    order={order}
                    dunningTargetId={dunningTargetId}
                    onDunning={(plan) => setDunningPlanId(plan.receivablePlanId)}
                  />
                ),
              },
              {
                key: 'txns',
                label: '支付流水',
                children: <PaymentTransactionsTab transactions={order.paymentTransactions} />,
              },
              {
                key: 'entitlement',
                label: '课时变化',
                children: <OrderLedgerTab orderId={orderId} studentId={order.studentId} />,
              },
              {
                key: 'entries',
                label: '收款入口',
                children: <PaymentEntriesTab orderId={orderId} />,
              },
              {
                key: 'refunds',
                label: `退款记录${order.refunds?.length ? `（${order.refunds.length}）` : ''}`,
                children: <RefundRecordsTab refunds={order.refunds ?? []} />,
              },
              {
                key: 'auditLogs',
                label: '操作历史',
                children: <AuditLogsTab logs={order.auditLogs} />,
              },
            ]}
          />
        </>
      )}
      <OfflinePaymentRecordDialog
        open={recording}
        orderId={orderId}
        onClose={() => setRecording(false)}
        onRecorded={() => {
          void fetchDetail();
          onOrderChanged?.();
        }}
      />
      {order && (
        <RefundRecordDialog
          open={refundOpen}
          orderId={orderId}
          orderNo={order.orderNo}
          studentName={order.studentName}
          paidAmountMinor={order.paidAmount}
          netReceivedAmountMinor={order.netReceivedAmount}
          refundedAmountMinor={order.refundedAmount}
          orderStatusLabel={ORDER_STATUS_LABEL[order.status] ?? order.status}
          operatorName={currentStaffName}
          onClose={() => setRefundOpen(false)}
          onRefunded={() => {
            void fetchDetail();
            onOrderChanged?.();
          }}
        />
      )}
      {paymentEntryType && order && (
        <PaymentEntryDialog
          open
          orderId={orderId}
          receivablePlanId={order.currentReceivablePlan?.receivablePlanId}
          entryType={paymentEntryType}
          orderContext={{
            receivableAmountMinor: order.receivableAmount,
            depositRatio: order.productSnapshot.depositRatio,
            currentReceivablePlan: order.currentReceivablePlan
              ? {
                type: order.currentReceivablePlan.type,
                amountMinor: order.currentReceivablePlan.amount,
                paidAmountMinor: order.currentReceivablePlan.paidAmount,
              }
              : null,
          }}
          onClose={() => setPaymentEntryType(null)}
          onPaid={() => {
            void fetchDetail();
            onOrderChanged?.();
          }}
        />
      )}
      <EnrollmentDetailDrawer
        applicationId={enrollmentId}
        onClose={() => setEnrollmentId(null)}
        onChanged={() => undefined}
      />
      {dunningPlan && order && (
        <DunningModal
          open
          orderId={orderId}
          order={order}
          plan={dunningPlan}
          onClose={() => setDunningPlanId(null)}
        />
      )}
    </DetailDrawer>
  );
}

function offlinePaymentEntryDisabledReason(order: OrderDetail): string | null {
  if (order.status === 'CANCELLED') return '订单已取消';
  if (order.status === 'EXPIRED') return '订单已过期';
  if (order.status === 'PAID') return '订单已支付';
  if (order.outstandingAmount <= 0) return '当前无待付金额';
  if (order.status === 'PARTIALLY_PAID' && !order.currentReceivablePlan) return '当前无待付节点';
  return null;
}

/** 订单操作历史（order-manual-refund SC-12/SC-34：billing-order 与 billing-order-refund 共存展示）。 */
function AuditLogsTab({ logs }: { logs: OrderDetail['auditLogs'] }) {
  if (!logs.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无操作记录" />;
  }
  return (
    <Table
      size="small"
      rowKey="auditLogId"
      dataSource={logs}
      pagination={{ pageSize: 10, showSizeChanger: false }}
      columns={[
        {
          title: '时间',
          width: 170,
          render: (_, log) => new Date(log.operatedAt).toLocaleString('zh-CN'),
        },
        {
          title: '操作人',
          width: 130,
          render: (_, log) => log.operatorName ?? log.operatorId.slice(0, 8),
        },
        {
          title: '动作',
          width: 120,
          render: (_, log) =>
            log.action === 'register_refund' ? (
              <Text type="danger">{AUDIT_ACTION_LABEL[log.action] ?? log.action}</Text>
            ) : (
              (AUDIT_ACTION_LABEL[log.action] ?? log.action)
            ),
        },
        {
          title: '变更（前值 → 后值）',
          width: 180,
          render: (_, log) => {
            if (!log.beforeValue && !log.afterValue) return '—';
            try {
              const before = JSON.parse(log.beforeValue ?? '{}') as Record<string, unknown>;
              const after = JSON.parse(log.afterValue ?? '{}') as Record<string, unknown>;
              if (before['netReceivedAmountMinor'] !== undefined) {
                return (
                  <span data-testid="audit-refund-delta">
                    实收合计 {Number(before['netReceivedAmountMinor']) / 100} →{' '}
                    {Number(after['netReceivedAmountMinor']) / 100} 元
                  </span>
                );
              }
              return '—';
            } catch {
              return '—';
            }
          },
        },
        {
          title: '业务原因',
          dataIndex: 'reason',
          width: 160,
          render: (v: string | null, log) =>
            v ??
            (typeof log.detail?.['reason'] === 'string' && log.detail['reason']
              ? (log.detail['reason'] as string)
              : '—'),
        },
        {
          title: '摘要',
          render: (_, log) => {
            const detail = log.detail ?? {};
            if (log.action === 'register_refund') {
              return (
                <span data-testid="audit-refund-summary">
                  <Text type="danger">
                    退款 <MoneyDisplay amountInCents={Number(detail['amountMinor'] ?? 0)} />
                  </Text>
                  {typeof detail['channel'] === 'string' ? `；渠道 ${REFUND_CHANNEL_LABEL[detail['channel'] as string] ?? detail['channel']}` : ''}
                  {typeof detail['refundNo'] === 'string' ? `；${detail['refundNo']}` : ''}
                </span>
              );
            }
            return typeof detail['reason'] === 'string' && detail['reason'] ? detail['reason'] : '—';
          },
        },
      ]}
    />
  );
}

/** 操作历史动作中文标签（未登记动作回退原始 action）。 */
const AUDIT_ACTION_LABEL: Record<string, string> = {
  register_refund: '登记退款',
  cancel: '取消订单',
  record_offline_payment: '线下收款登记',
};


/** 退款记录 Tab（两源合并，UI 方案 v0.7 ADM-10；金额红色负向）。 */
function RefundRecordsTab({ refunds }: { refunds: OrderDetail['refunds'] }) {
  if (!refunds.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无退款记录" />;
  }
  return (
    <Table
      size="small"
      rowKey="refundNo"
      dataSource={refunds}
      pagination={false}
      columns={[
        { title: '退款单号', dataIndex: 'refundNo', width: 200 },
        {
          title: '来源',
          width: 90,
          render: (_, r) => (r.source === 'MANUAL' ? '人工登记' : '差错退款'),
        },
        {
          title: '渠道',
          width: 80,
          render: (_, r) => REFUND_CHANNEL_LABEL[r.channel as keyof typeof REFUND_CHANNEL_LABEL] ?? r.channel ?? '—',
        },
        {
          title: '渠道退款单号',
          dataIndex: 'channelRefNo',
          width: 160,
          render: (v: string | null) => v ?? '—',
        },
        {
          title: '金额',
          width: 110,
          render: (_, r) => (
            <Text type="danger" data-testid="refund-record-amount">
              -<MoneyDisplay amountInCents={r.amountMinor} />
            </Text>
          ),
        },
        { title: '状态', dataIndex: 'status', width: 80, render: (v: string) => (v === 'REGISTERED' ? '已登记' : v === 'SUCCESS' ? '成功' : v) },
        { title: '经办人', dataIndex: 'operatorName', width: 90, render: (v: string | null) => v ?? '—' },
        {
          title: '退款时间',
          width: 160,
          render: (_, r) => new Date(r.refundedAt).toLocaleString('zh-CN'),
        },
        { title: '退款原因', dataIndex: 'reason', render: (v: string | null) => v ?? '—' },
      ]}
    />
  );
}

/** 退款渠道中文标签（与 RefundRecordDialog 同映射）。 */
const REFUND_CHANNEL_LABEL: Record<string, string> = {
  WECHAT: '微信',
  ALIPAY: '支付宝',
  CASH: '现金',
  BANK_TRANSFER: '银行转账',
  OTHER: '其他',
};
